import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Skull, Swords, Zap, Sparkles } from "lucide-react";
import BossSprite from "@/components/BossSprite";
import T from "@/components/T";

/**
 * Weekly world boss. Every player can chip damage.
 * Actions:
 *  - Strike (3 Explore Points) — basic contribution attack
 *  - Special (6 Vigor)        — heavy blow, only martial classes (Warrior/Rogue/Guardian)
 *  - Magic  (8 Mana)          — arcane burst, only mage classes (Wizard/Bard/Guardian)
 * Contribution damage is tracked for the reward split at kill time.
 */
export default function WorldBossView({ ch, setCh, onBossKilled }) {
  const [boss, setBoss] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => setBoss(await api.worldBoss());
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, []);

  const strike = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.worldBossHit(action);
      setCh(res.character);
      await refresh();
      // If the killing blow just landed, kick off the boss cinematic.
      if (res.boss_kill_report && onBossKilled) {
        onBossKilled(res.boss_kill_report);
      } else if (res.drop) {
        toast.success(`Killing blow! Received ${res.drop.name}`);
      } else if (res.boss_hp <= 0) {
        toast.success("The world boss falls! Rewards distributed.");
      } else {
        toast(`Dealt ${res.damage_dealt} damage`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!boss) return <div className="panel p-4 text-center text-[#666]">Peering into the deep...</div>;

  const pct = Math.max(0, Math.min(100, (boss.hp / boss.max_hp) * 100));
  const totalDmg = (boss.contributors || []).reduce((s, c) => s + c.damage, 0) || 1;
  const canMartial = (ch.max_vigor || 0) > 0;
  const canMage = (ch.max_mana || 0) > 0;
  const dead = boss.hp <= 0;

  return (
    <div className="space-y-2" data-testid="worldboss-view">
      <div className="panel p-4 relative">
        <div className="absolute -top-3 left-4 px-3 bg-[#050505] text-[#8A0303] font-heading text-xs tracking-[0.3em]">
          <T>WEEKLY WORLD BOSS</T>
        </div>
        <div className="flex flex-col items-center gap-2 mb-3">
          <BossSprite name={boss.name} pct={pct} size={220} image={boss.image} animations={boss.animations} />
          <div className="text-center">
            <div className="font-heading text-xl tracking-widest uppercase text-[#E33333]" data-testid="boss-name" style={{ textShadow: "0 0 8px rgba(138,3,3,0.5)" }}>
              {boss.name}
            </div>
            <div className="vbar mt-2 mx-auto" style={{ height: 14, maxWidth: 320 }}>
              <span style={{ width: `${pct}%`, background: "#8A0303" }} />
            </div>
            <div className="font-mono text-[10px] text-[#8c8c8c] mt-1" data-testid="boss-hp">
              {boss.hp}/{boss.max_hp} HP · Reward pool: {boss.reward_gold}g / {boss.reward_xp}xp
            </div>
          </div>
        </div>

        {/* Action row */}
        <div className="grid grid-cols-3 gap-2">
          <button
            data-testid="boss-strike"
            onClick={() => strike("attack")}
            disabled={busy || dead || ch.explore_points < 3}
            className="py-3 border-2 border-[#8A0303] hover:border-[#E33333] text-[#E33333] font-heading text-xs tracking-widest uppercase disabled:opacity-30 flex flex-col items-center gap-1"
          >
            <Swords className="w-4 h-4" strokeWidth={1.5} />
            <span>Strike</span>
            <span className="font-mono text-[9px] text-[#E5C34A]">3 Explore</span>
          </button>
          <button
            data-testid="boss-special"
            onClick={() => strike("special")}
            disabled={busy || dead || !canMartial || (ch.vigor || 0) < 6}
            title={canMartial ? "" : "Requires a martial class (Warrior / Rogue / Guardian)"}
            className="py-3 border-2 border-[#B89C3A] hover:border-[#E5C34A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase disabled:opacity-30 flex flex-col items-center gap-1"
          >
            <Zap className="w-4 h-4" strokeWidth={1.5} />
            <span>Special</span>
            <span className="font-mono text-[9px] text-[#B89C3A]">6 Vigor</span>
          </button>
          <button
            data-testid="boss-magic"
            onClick={() => strike("magic")}
            disabled={busy || dead || !canMage || (ch.mana || 0) < 8}
            title={canMage ? "" : "Requires a mage class (Wizard / Bard / Guardian)"}
            className="py-3 border-2 border-[#4A7BC7] hover:border-[#6FA0FF] text-[#4A7BC7] font-heading text-xs tracking-widest uppercase disabled:opacity-30 flex flex-col items-center gap-1"
          >
            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            <span>Magic</span>
            <span className="font-mono text-[9px] text-[#4A7BC7]">8 Mana</span>
          </button>
        </div>

        <div className="font-mono text-[9px] text-[#666] tracking-widest uppercase mt-2 text-center">
          Abilities hit harder than a basic strike — but the pool matters
        </div>
      </div>

      <div className="panel p-3" data-testid="boss-leaderboard">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] mb-2"><T>Contributors</T></div>
        {(boss.contributors || []).length === 0 && (
          <p className="italic text-[#666] text-xs">Nobody has drawn blood yet.</p>
        )}
        <div className="space-y-1">
          {boss.contributors.slice(0, 15).map((c, i) => (
            <div key={c.user_id} className="flex justify-between items-center text-xs font-mono">
              <span className="text-[#E2DFD8]">
                <span className="text-[#B89C3A] w-6 inline-block">{i + 1}.</span> {c.name}
              </span>
              <span className="text-[#E33333]">
                {c.damage} dmg <span className="text-[#666]">({Math.round((c.damage / totalDmg) * 100)}%)</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
